// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, useState, type ReactNode } from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { NavigationRail } from "../components/layout/NavigationRail";
import { SignedOutBanner } from "../components/layout/SignedOutBanner";
import { TopBanner } from "../components/layout/TopBanner";
import { useOnboardingStatus } from "../context/OnboardingContext";
import { TutorialHost } from "../features/tutorial/components/TutorialHost";
import { useTutorial } from "../features/tutorial/context/TutorialContext";
import { useActivityHeartbeat } from "../hooks/useActivityHeartbeat";
import { useAuthSession } from "../hooks/useAuthSession";
import { useAppearanceAccountSync } from "../features/settings/useAppearanceAccountSync";
import { useLocaleAccountSync } from "../features/settings/useLocaleAccountSync";
import { usePendingDelegationCount } from "../features/delegations/hooks/usePendingDelegationCount";
import type { AppRoute } from "../types/routing";
import { isDevPreviewMode } from "../utils/devEnv";
import { useTeamsEnabled } from "../context/FeatureFlagsContext";

export const DEV_PREVIEW_USER_LABEL = "Demo Agent";

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    display: "grid",
    gridTemplateRows: "auto 1fr",
    gap: tokens.spacingVerticalS,
    "@media (max-width: 980px)": {
      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    },
  },
  shell: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: tokens.spacingHorizontalM,
    alignItems: "start",
    "@media (max-width: 980px)": {
      gridTemplateColumns: "1fr",
      gap: tokens.spacingVerticalS,
    },
  },
  shellFull: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "0",
  },
  stage: {
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: "var(--riddle-surface-secondary)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: tokens.shadow8,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    display: "grid",
    gap: tokens.spacingVerticalM,
    minHeight: "480px",
    alignContent: "start",
    "@media (max-width: 980px)": {
      padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    },
  },
});

interface AppShellLayoutProps {
  route: AppRoute;
  children: ReactNode;
}

export function AppShellLayout({ route, children }: AppShellLayoutProps) {
  const styles = useStyles();
  const { activeAccount } = useAuthSession();
  useAppearanceAccountSync();
  useLocaleAccountSync();
  const { isOnboarded } = useOnboardingStatus();
  const { start: startTutorial, canAutoStart } = useTutorial();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const isOnboardingRoute = route.key === "onboarding";
  const { teams } = useTeamsEnabled();
  const pendingDelegationCount = usePendingDelegationCount(teams && !isOnboardingRoute);
  useActivityHeartbeat(!isDevPreviewMode);
  const userLabel =
    activeAccount?.name ??
    activeAccount?.username ??
    (isDevPreviewMode ? DEV_PREVIEW_USER_LABEL : "Unknown User");

  useEffect(() => {
    if (isDevPreviewMode || isOnboardingRoute) {
      return;
    }
    if (isOnboarded && canAutoStart("workspace")) {
      startTutorial("workspace");
    }
  }, [isOnboardingRoute, isOnboarded, canAutoStart, startTutorial]);

  return (
    <main className={styles.root}>
      {isOnboardingRoute ? (
        <TopBanner isAuthenticated userLabel={userLabel} />
      ) : (
        <TopBanner
          isAuthenticated
          userLabel={userLabel}
          onMenuButtonClick={() => setIsMobileNavOpen((previous) => !previous)}
        />
      )}
      <SignedOutBanner />

      <section className={isOnboardingRoute ? styles.shellFull : styles.shell}>
        {!isOnboardingRoute && (
          <NavigationRail
            activeRouteKey={route.key}
            isAuthenticated
            open={isMobileNavOpen}
            onOpenChange={setIsMobileNavOpen}
            delegationBadgeCount={pendingDelegationCount}
          />
        )}

        <section className={styles.stage} aria-label="Active workspace stage">
          {children}
        </section>
      </section>

      <TutorialHost />
    </main>
  );
}
